import { useMemo, useState } from 'react';
import { Check, ChevronUp, ChevronDown } from 'lucide-react';

function NumberBadge({ index, status }) {
    if (status === 'completed') {
        return (
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
            </div>
        );
    }
    if (status === 'in_progress') {
        return (
            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-semibold text-white">{index}</span>
            </div>
        );
    }
    return (
        <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-medium text-gray-400">{index}</span>
        </div>
    );
}

function TodoItem({ todo, index }) {
    const label = todo.status === 'in_progress'
        ? (todo.activeForm || todo.content)
        : todo.content;

    const textClass = todo.status === 'completed'
        ? 'text-gray-400 line-through'
        : todo.status === 'in_progress'
            ? 'text-gray-800 font-medium'
            : 'text-gray-500';

    return (
        <div className="flex items-start gap-2 py-1.5">
            <div className="mt-[1px]"><NumberBadge index={index} status={todo.status} /></div>
            <span className={`text-[13px] leading-tight ${textClass}`}>{label}</span>
        </div>
    );
}

export default function TodoProgressPanel({ todos }) {
    const counts = useMemo(() => {
        if (!todos) return null;
        const completed = todos.filter(t => t.status === 'completed').length;
        return { completed, total: todos.length };
    }, [todos]);

    if (!todos || todos.length === 0) return null;

    return (
        <div className="border border-gray-200 rounded-2xl bg-white shadow-sm">
            <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm font-semibold text-gray-700">Danh sách công việc</span>
                {counts && (
                    <span className="text-xs text-gray-400 tabular-nums">
                        {counts.completed} / {counts.total}
                    </span>
                )}
            </div>

            <div className="px-4 pb-3 space-y-0">
                {todos.map((todo, index) => (
                    <TodoItem key={index} todo={todo} index={index + 1} />
                ))}
            </div>
        </div>
    );
}

export function TodoProgressCompact({ todos }) {
    const [isExpanded, setIsExpanded] = useState(false);

    const counts = useMemo(() => {
        if (!todos) return null;
        const completed = todos.filter(t => t.status === 'completed').length;
        return { completed, total: todos.length };
    }, [todos]);

    const currentTodo = useMemo(() => {
        if (!todos) return null;
        return todos.find(t => t.status === 'in_progress') || todos[todos.length - 1];
    }, [todos]);

    if (!todos || todos.length === 0) return null;

    const allCompleted = counts && counts.completed === counts.total;
    const displayLabel = currentTodo
        ? (currentTodo.status === 'in_progress' ? (currentTodo.activeForm || currentTodo.content) : currentTodo.content)
        : '';

    return (
        <div className="xl:hidden mb-2 animate-slide-in-right">
            <div className="border border-gray-200 rounded-2xl bg-white shadow-sm overflow-hidden">
                <button
                    onClick={() => setIsExpanded(prev => !prev)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left"
                >
                    {isExpanded ? (
                        <>
                            <span className="flex-1 text-sm text-gray-800 font-semibold truncate">Danh sách công việc</span>
                            {counts && (
                                <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
                                    {counts.completed} / {counts.total}
                                </span>
                            )}
                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        </>
                    ) : (
                        <>
                            {allCompleted ? (
                                <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                                </div>
                            ) : (
                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                            )}
                            <span className="flex-1 text-sm text-gray-600 truncate">{displayLabel}</span>
                            {counts && (
                                <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
                                    {counts.completed} / {counts.total}
                                </span>
                            )}
                            <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        </>
                    )}
                </button>

                {isExpanded && (
                    <div className="px-4 pb-3">
                        {todos.map((todo, index) => (
                            <TodoItem key={index} todo={todo} index={index + 1} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
